/**
 * Stripe Production Configuration
 * Secure configuration for production payment processing
 */

// Environment-based configuration
const isDevelopment = __DEV__;
const isProduction = !isDevelopment;

const DEFAULT_PAYMENT_API_ROOT = 'https://bookapt-payment-api.onrender.com';

/** Same host resolution as webStripeCheckout: env override, else production API (never assume local backend is running). */
function resolvePaymentApiRoot(): string {
  const fromEnv =
    typeof process !== 'undefined'
      ? String(
          process.env.REACT_APP_PAYMENT_API_URL ||
            process.env.REACT_APP_API_BASE_URL ||
            ''
        ).trim()
      : '';
  if (!fromEnv) return DEFAULT_PAYMENT_API_ROOT;
  let root = fromEnv.replace(/\/+$/, '');
  if (root.endsWith('/api')) {
    root = root.slice(0, -4).replace(/\/+$/, '');
  }
  return root || DEFAULT_PAYMENT_API_ROOT;
}

const paymentApiRoot = resolvePaymentApiRoot();
const apiBase = `${paymentApiRoot}/api`;

export const STRIPE_CONFIG = {
  // API Keys - Use environment variables in production
  publishableKey: isDevelopment 
    ? 'pk_test_51SAyMcCdQt80fPgMBICuDhCI1Hl2oTJWXpRr6YqYuFOmoae5iXHLJC5TGYWtupV7U6ngKjPutxTlgZIiRTGtgfAa00tRFiUEve' // Test key
    : process.env.STRIPE_PUBLISHABLE_KEY || 'pk_live_51NkZeAGAjH1SWAZv6ppceud8oxqE4aozs1zjtaVMjfFeKRwN64PriFGTAloy1VKZglQ3iw1b1obYkv4uPB8zCiLx00TIPGqtyK', // Live key
  
  // Connect settings for business owner payouts
  connectClientId: isDevelopment
    ? 'ca_test_YOUR_TEST_CONNECT_CLIENT_ID' // Test Connect
    : process.env.STRIPE_CONNECT_CLIENT_ID || 'ca_live_YOUR_LIVE_CONNECT_CLIENT_ID', // Live Connect
  
  // Payment API base (see resolvePaymentApiRoot; override with REACT_APP_PAYMENT_API_URL for local backend)
  apiEndpoint: apiBase,
  
  // Apple Pay configuration
  merchantIdentifier: 'merchant.com.bookapt',
  merchantName: 'BookAPT',
  
  // Payment settings
  currency: 'usd',
  country: 'US',
  
  // Security settings
  enable3DSecure: true,
  enableRadar: true, // Stripe's fraud detection
  
  // Business settings
  platformFeePercent: 2.9, // BookAPT's platform fee
  minimumCharge: 0.50, // Minimum charge amount
  maximumCharge: 10000, // Maximum charge amount ($100)
};

// Validation function
export const validateStripeConfig = (): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!STRIPE_CONFIG.publishableKey) {
    errors.push('Stripe publishable key is required');
  }
  
  if (isProduction && STRIPE_CONFIG.publishableKey.startsWith('pk_test_')) {
    errors.push('Using test key in production environment');
  }
  
  if (!STRIPE_CONFIG.apiEndpoint) {
    errors.push('API endpoint is required');
  }
  
  if (!STRIPE_CONFIG.connectClientId) {
    errors.push('Stripe Connect client ID is required for business payouts');
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
};

// Helper function to check if running in production
export const isProductionMode = (): boolean => {
  return isProduction && !STRIPE_CONFIG.publishableKey.startsWith('pk_test_');
};

// Helper function to get environment info
export const getEnvironmentInfo = () => {
  return {
    isDevelopment,
    isProduction,
    isTestMode: STRIPE_CONFIG.publishableKey.startsWith('pk_test_'),
    apiEndpoint: STRIPE_CONFIG.apiEndpoint,
    hasConnect: !!STRIPE_CONFIG.connectClientId
  };
};




